import { useRef, useState, useEffect, useMemo } from "react";
import { SlideshowLightbox } from "lightbox.js-react";

export function FilePreviewModal({ selectedFile, fileUrl, onClose }) {
  const modalRef = useRef(null);
  const isDragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: null, y: null });

  const isImage = selectedFile?.type?.startsWith("image/");
  const isPdf = selectedFile?.type === "application/pdf";
  const filename = selectedFile?.name || selectedFile?.filename || "";
  const isImageFile = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);

  const pdfIframe = useMemo(() => (
    <iframe
      src={URL.createObjectURL(selectedFile)}
      title="PDF preview"
      className="w-full h-full border-0"
    />
  ), [selectedFile]);

  useEffect(() => {
    const el = modalRef.current;
    if (!el || pos.x !== null) return;
    const x = window.innerWidth / 2 - el.offsetWidth / 2;
    const y = window.innerHeight / 2 - el.offsetHeight / 2;
    setPos({ x, y });
  }, []);

  const onMouseDown = (e) => {
    isDragging.current = true;
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      setPos({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y,
      });
    };
    const onUp = () => (isDragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  const handleDownload = () => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = filename || "image";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
const handleOpenInNewTab = () => {
  const url = URL.createObjectURL(selectedFile);
  window.open(url, "_blank");
};
  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-15 z-40"
        onClick={onClose}
      />

      <div
        ref={modalRef}
        className="fixed z-50 w-[60%] max-w-3xl rounded-xl overflow-hidden border border-gray-200 shadow-xl"
        style={
          pos.x !== null
            ? { left: pos.x, top: pos.y }
            : {
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }
        }
      >
        {/* HEADER */}
        <div
          className="flex items-center gap-3 px-4 py-3 bg-[#1e2d40] cursor-grab active:cursor-grabbing select-none"
          onMouseDown={onMouseDown}
        >
          <div className="grid grid-cols-2 gap-1 opacity-40">
            {[...Array(6)].map((_, i) => (
              <span key={i} className="w-1 h-1 rounded-full bg-white block" />
            ))}
          </div>

          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-base">
            {isImageFile ? "🖼️" : isPdf ? "📄" : "📎"}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{filename}</p>
            <p className="text-white/50 text-xs">Aperçu du fichier</p>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm flex items-center justify-center transition"
          >
            ✕
          </button>
        </div>

        {/* CONTENT */}
        <div
          className="bg-gray-50 flex items-center justify-center"
          style={{ height: "70vh" }}
        >
          {(isImage || isImageFile) && (
            <SlideshowLightbox className="mx-auto">
              <img
                src={fileUrl}
                alt="Aperçu"
                className="block max-w-full max-h-[70vh] w-auto h-auto rounded-lg cursor-zoom-in object-contain"
              />
            </SlideshowLightbox>
          )}

          {isPdf && pdfIframe}

          {!isImage && !isImageFile && !isPdf && (
            <div className="text-center text-gray-400">
              <p className="text-4xl mb-3">📎</p>
              <p className="text-sm">
                Aperçu non disponible pour ce format.
              </p>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-100">
          <span className="text-xs text-gray-400">{filename}</span>
          <div className="flex items-center gap-2">
    {(isImage || isImageFile) && (
      <button
        onClick={handleDownload}
        className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
      >
        Télécharger
      </button>
    )}
{/* PDF OPEN IN NEW TAB */}
  {/* IMAGE + PDF OPEN */}
  {(isImage || isImageFile || isPdf) && (
    <button
      onClick={handleOpenInNewTab}
      className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition"
    >
      Ouvrir dans un onglet
    </button>
  )}
    <button
      onClick={onClose}
      className="px-4 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
    >
      Fermer
    </button>
  </div>
        </div>
      </div>
    </>
  );
}